import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../../utils/seo';
import Icon from '../../../components/base/Icon';

const QUOTE_FORM_URL = 'https://readdy.ai/api/form/d6p51mjlgirdouph35tg';

const SERVICE_OPTIONS = [
  { value: '', label: 'Select a service...' },
  { value: 'Fence Installation', label: 'Fence Installation' },
  { value: 'Fence Repair', label: 'Fence Repair' },
  { value: 'Gate Installation', label: 'Gate Installation' },
  { value: 'Fence Replacement', label: 'Fence Replacement' },
  { value: 'Other Fence Work', label: 'Other Fence Work' },
];

interface QuoteFormState {
  full_name: string;
  phone: string;
  email: string;
  service_needed: string;
  project_details: string;
}

const emptyForm = (): QuoteFormState => ({
  full_name: '',
  phone: '',
  email: '',
  service_needed: '',
  project_details: '',
});

function FenceQuoteForm({ formId }: { formId: string }) {
  const [values, setValues] = useState<QuoteFormState>(emptyForm());
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validate = (): string => {
    if (!values.full_name.trim()) return 'Full name is required.';
    if (!values.phone.trim()) return 'Phone number is required.';
    if (!values.email.trim()) return 'Email address is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) return 'Please enter a valid email address.';
    if (!values.service_needed) return 'Please select a service.';
    if (!values.project_details.trim()) return 'Please describe your project.';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setErrorMsg(validationError);
      setStatus('error');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      const params = new URLSearchParams();
      (Object.keys(values) as (keyof QuoteFormState)[]).forEach((key) => {
        params.append(key, values[key]);
      });
      const res = await fetch(QUOTE_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (!res.ok) throw new Error('Submission failed');
      setStatus('success');
      setValues(emptyForm());
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again or call us at (208) 204-6331.');
    }
  };

  const inputClass = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B45309] focus:border-transparent text-sm';

  return (
    <form onSubmit={handleSubmit} data-readdy-form id={formId} noValidate>
      {status === 'success' && (
        <div className="mb-5 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm flex items-start gap-2" role="alert">
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="check" className="w-4 h-4" /></span>
          <span>Thanks — we received your quote request and will contact you within 24 hours.</span>
        </div>
      )}
      {status === 'error' && errorMsg && (
        <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2" role="alert">
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="circle-exclamation" className="w-4 h-4" /></span>
          <span>{errorMsg}</span>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor={`${formId}-full_name`} className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
          <input type="text" id={`${formId}-full_name`} name="full_name" required value={values.full_name} onChange={handleChange} className={inputClass} placeholder="John Smith" />
        </div>
        <div>
          <label htmlFor={`${formId}-phone`} className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
          <input type="tel" id={`${formId}-phone`} name="phone" required value={values.phone} onChange={handleChange} className={inputClass} placeholder="(208) 555-0123" />
        </div>
      </div>
      <div className="mb-4">
        <label htmlFor={`${formId}-email`} className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
        <input type="email" id={`${formId}-email`} name="email" required value={values.email} onChange={handleChange} className={inputClass} placeholder="john@example.com" />
      </div>
      <div className="mb-4">
        <label htmlFor={`${formId}-service_needed`} className="block text-sm font-medium text-gray-700 mb-1">Service Needed *</label>
        <select id={`${formId}-service_needed`} name="service_needed" required value={values.service_needed} onChange={handleChange} className={`${inputClass} cursor-pointer`}>
          {SERVICE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div className="mb-6">
        <label htmlFor={`${formId}-project_details`} className="block text-sm font-medium text-gray-700 mb-1">Project Details *</label>
        <textarea id={`${formId}-project_details`} name="project_details" required rows={3} maxLength={500} value={values.project_details} onChange={handleChange} className={`${inputClass} resize-none`} placeholder="Describe your fence project — material preference, linear footage, property type..." />
        <p className="text-xs text-gray-500 mt-1">{values.project_details.length}/500 characters</p>
      </div>
      <button type="submit" disabled={status === 'submitting'} className="w-full bg-[#B45309] hover:bg-[#92400E] disabled:opacity-60 text-white px-6 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer">
        {status === 'submitting' ? 'Sending...' : 'Request Free Fence Quote'}
      </button>
    </form>
  );
}

const materials = [
  {
    name: 'Wood',
    icon: 'tree',
    image: 'https://public.readdy.ai/ai/img_res/a143899ded06bb7f0ce5db3e65c3b7d5.jpg',
    pros: ['Natural look and warmth', 'Highly customizable height & style', 'Easy to repair individual boards', 'Cost-effective upfront', 'Paintable or stainable'],
    cons: ['Requires periodic staining or sealing', 'Can warp or rot if untreated', 'Shorter lifespan than vinyl'],
    lifespan: '15–30 years (with maintenance)',
    bestFor: 'Privacy fences, decorative picket fences, homeowners who want a classic look',
  },
  {
    name: 'Vinyl',
    icon: 'layer-group',
    image: 'https://readdy.ai/api/search-image?query=clean%20white%20vinyl%20privacy%20fence%20surrounding%20a%20well-maintained%20residential%20yard%20in%20Boise%20Idaho%20bright%20white%20panels%20modern%20suburban%20neighborhood%20clear%20sky%20low%20maintenance%20appearance&width=600&height=400&seq=fence-vinyl-mat-01&orientation=landscape',
    pros: ['Very low maintenance', 'Won\'t rot, warp, or rust', 'Holds color well over time', 'Easy to clean with a hose', 'Long lifespan'],
    cons: ['Higher upfront cost than wood', 'Can crack in extreme cold', 'Fewer style options than wood', 'Harder to repair single sections'],
    lifespan: '20–40 years',
    bestFor: 'Homeowners wanting minimal upkeep, pool enclosures, HOA-compliant installs',
  },
  {
    name: 'Chain Link',
    icon: 'link',
    image: 'https://readdy.ai/api/search-image?query=galvanized%20chain%20link%20fence%20around%20a%20residential%20property%20in%20Boise%20Idaho%20with%20green%20grass%20and%20clear%20sky%20functional%20durable%20metal%20fencing%20suburban%20yard%20practical%20security%20fencing&width=600&height=400&seq=fence-chainlink-mat-01&orientation=landscape',
    pros: ['Most affordable option', 'Very durable and long-lasting', 'Low maintenance', 'Good for large perimeters', 'Allows airflow and visibility'],
    cons: ['Minimal privacy', 'Less decorative appeal', 'Can rust without coating', 'Not ideal for front yards aesthetically'],
    lifespan: '20–30 years (galvanized)',
    bestFor: 'Dog runs, back yards, large properties, utility areas, budget-conscious projects',
  },
  {
    name: 'Gates',
    icon: 'door-open',
    image: 'https://readdy.ai/api/search-image?query=elegant%20wooden%20double%20gate%20with%20decorative%20hardware%20installed%20in%20a%20residential%20fence%20in%20Boise%20Idaho%20warm%20wood%20tones%20arched%20top%20professional%20installation%20suburban%20driveway%20entry&width=600&height=400&seq=fence-gate-mat-01&orientation=landscape',
    pros: ['Custom sizing available', 'Can match any fence material', 'Manual or automatic options', 'Adds security and curb appeal'],
    cons: ['Hinges and latches need periodic adjustment', 'Heavier gates may sag over time', 'Automatic openers add cost'],
    lifespan: 'Matches fence material; hardware 10–20 years',
    bestFor: 'Driveways, side yards, pool access, any fence needing controlled entry',
  },
];

const pricingRows = [
  {
    type: 'Wood Privacy Fence (6 ft)',
    low: '$18–$25',
    high: '$35–$50+',
    unit: 'per linear ft installed',
    dependsOn: 'Cedar vs. pine, board style, site prep needed',
  },
  {
    type: 'Wood Picket Fence (3–4 ft)',
    low: '$12–$18',
    high: '$25–$35',
    unit: 'per linear ft installed',
    dependsOn: 'Picket spacing, post spacing, decorative caps',
  },
  {
    type: 'Vinyl Privacy Fence (6 ft)',
    low: '$25–$35',
    high: '$45–$65+',
    unit: 'per linear ft installed',
    dependsOn: 'Panel style, brand quality, post depth required',
  },
  {
    type: 'Chain Link Fence (4–6 ft)',
    low: '$10–$15',
    high: '$20–$30',
    unit: 'per linear ft installed',
    dependsOn: 'Height, gauge of wire, galvanized vs. coated',
  },
  {
    type: 'Wood Gate (single, walk-through)',
    low: '$200–$400',
    high: '$600–$1,000+',
    unit: 'per gate installed',
    dependsOn: 'Width, wood species, hardware quality',
  },
  {
    type: 'Double Drive Gate (wood/vinyl)',
    low: '$600–$1,200',
    high: '$1,800–$3,500+',
    unit: 'per gate installed',
    dependsOn: 'Width, material, automatic opener add-on',
  },
  {
    type: 'Post Replacement',
    low: '$150–$250',
    high: '$350–$500+',
    unit: 'per post',
    dependsOn: 'Concrete removal, post depth, material',
  },
  {
    type: 'Fence Repair (boards/panels)',
    low: '$100–$200',
    high: '$300–$600+',
    unit: 'per repair visit',
    dependsOn: 'Number of boards, material match, travel',
  },
];

const delayFactors = [
  {
    icon: 'cloud-rain',
    title: 'Weather',
    desc: 'Rain and frozen ground can delay post-setting. Concrete needs dry conditions to cure properly. Idaho winters can push outdoor installs.',
  },
  {
    icon: 'map',
    title: 'Utility Locates (811)',
    desc: 'Idaho law requires calling 811 before digging. Utility marking typically takes 2–3 business days and must happen before any post holes are dug.',
  },
  {
    icon: 'file-contract',
    title: 'HOA Approval',
    desc: 'Many Treasure Valley HOAs require written approval before fence installation. Review times vary — some take 1 week, others 30+ days.',
  },
  {
    icon: 'clipboard-list',
    title: 'Permit Processing',
    desc: 'If a permit is required, Boise and Ada County processing times vary. Simple residential fences are often approved quickly, but allow extra time.',
  },
  {
    icon: 'truck',
    title: 'Material Lead Times',
    desc: 'Cedar and specialty wood can have supply delays. Vinyl panels are typically in stock locally, but custom colors or sizes may take 1–2 weeks.',
  },
  {
    icon: 'ruler-combined',
    title: 'Property Line Disputes',
    desc: 'If your property lines are unclear, a survey may be needed before installation. This can add days or weeks depending on surveyor availability.',
  },
];

const faqs = [
  {
    q: 'Do I need a permit to install a fence in Boise?',
    shortA: 'It depends on fence height and location.',
    fullA: 'In Boise, fences under 6 feet in the rear or side yard typically do not require a permit. Fences in the front yard or over 6 feet generally do. Rules vary by zone and HOA. Always check with the City of Boise Planning & Development Services or your HOA before starting. We can help you navigate this during the estimate process.',
  },
  {
    q: 'How long does fence installation take?',
    shortA: 'Most residential fences take 1–3 days.',
    fullA: 'A standard residential fence installation (100–200 linear feet) typically takes 1–3 days once we start. Larger properties, complex terrain, or custom gates can extend this. Delays can come from weather, utility locates, or material delivery. We\'ll give you a realistic timeline in your written estimate.',
  },
  {
    q: 'What\'s the best fence material for Boise\'s climate?',
    shortA: 'Cedar wood and vinyl both perform well here.',
    fullA: 'Boise\'s semi-arid climate with hot summers and cold winters is manageable for most fence materials. Cedar is naturally rot-resistant and handles temperature swings well. Vinyl is excellent for low maintenance but can become brittle in extreme cold. Chain link is the most weather-resistant overall. We\'ll recommend the best option based on your budget, HOA rules, and goals.',
  },
  {
    q: 'How do you handle property lines?',
    shortA: 'We install based on your property survey or markers.',
    fullA: 'We install fences based on the property lines you provide. If you\'re unsure of your exact property boundaries, we strongly recommend getting a survey before installation. Installing on a neighbor\'s property — even accidentally — can create legal and financial headaches. We can recommend local surveyors if needed.',
  },
  {
    q: 'Do you handle HOA fence approvals?',
    shortA: 'We can advise, but HOA submission is the homeowner\'s responsibility.',
    fullA: 'We\'re familiar with common HOA requirements in Treasure Valley communities and can help you understand what\'s typically required (material, color, height, setbacks). However, submitting the HOA application and getting approval is the homeowner\'s responsibility. We recommend starting that process early — some HOAs take 2–4 weeks to respond.',
  },
  {
    q: 'Can you match my existing fence style?',
    shortA: 'Usually yes, especially for wood and chain link.',
    fullA: 'For wood fences, we can typically match board style, height, and stain color closely. For vinyl, matching depends on the manufacturer and whether the same panel profile is still available. Chain link is easy to match. Bring photos of your existing fence to the estimate and we\'ll let you know what\'s achievable.',
  },
  {
    q: 'What\'s included in your fence estimate?',
    shortA: 'Labor, materials, post concrete, and cleanup.',
    fullA: 'Our written estimates include all labor, materials (posts, boards/panels, hardware), concrete for post setting, and site cleanup. We\'ll note any exclusions clearly — for example, if tree root removal or significant grading is needed, that may be a separate line item. No hidden fees.',
  },
  {
    q: 'Do you remove old fences before installing a new one?',
    shortA: 'Yes, we can include demo and haul-away.',
    fullA: 'Old fence removal and disposal can be included in your project scope. We\'ll add it as a line item in the estimate. If you\'d prefer to remove the old fence yourself to save money, that\'s fine too — just let us know before the estimate visit.',
  },
  {
    q: 'How far in advance should I book?',
    shortA: 'Spring and summer book out 2–4 weeks.',
    fullA: 'Spring and early summer are our busiest seasons for fence installation in Boise. During peak season (April–July), we typically book 2–4 weeks out. Fall and winter have more availability. We recommend reaching out as early as possible to lock in your preferred start date.',
  },
  {
    q: 'Are you licensed and insured for fence work in Idaho?',
    shortA: 'Yes — fully licensed and insured.',
    fullA: 'Fix Right Services is fully licensed and carries general liability insurance for all fence installation and repair work in Idaho. We\'re happy to provide proof of insurance before any work begins. Hiring an unlicensed contractor for fence work can void your homeowner\'s insurance coverage if something goes wrong.',
  },
];

export default function FenceServicePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<Set<number>>(new Set());

  const toggleFaq = (idx: number) => {
    setOpenFaq(openFaq === idx ? null : idx);
  };

  const toggleExpand = (idx: number) => {
    setExpandedFaq((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  useSEO({
    title: 'Boise Fence Installation & Repair | Fix Right Services Idaho',
    description: 'Professional fence installation and repair in Boise, Meridian, Nampa & Treasure Valley. Wood, vinyl, chain link & gates. Licensed, insured, free estimates. Call (208) 204-6331.',
    keywords: 'fence installation Boise Idaho, fence repair Boise, wood fence Boise, vinyl fence Treasure Valley, chain link fence Meridian, gate installation Boise, fence contractor Idaho',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/services/fence`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      'name': 'Fence Installation & Repair — Boise, Idaho',
      'description': 'Professional fence installation and repair services in Boise and the Treasure Valley. Wood, vinyl, chain link, and custom gates.',
      'provider': {
        '@type': 'LocalBusiness',
        'name': 'Fix Right Services',
        'telephone': '+12082046331',
        'url': import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com',
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': 'Boise',
          'addressRegion': 'ID',
          'addressCountry': 'US',
        },
      },
      'areaServed': ['Boise', 'Meridian', 'Nampa', 'Caldwell', 'Eagle', 'Star', 'Kuna', 'Middleton', 'Garden City'],
      'hasOfferCatalog': {
        '@type': 'OfferCatalog',
        'name': 'Fence Services',
        'itemListElement': [
          { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Wood Fence Installation' } },
          { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Vinyl Fence Installation' } },
          { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Chain Link Fence Installation' } },
          { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Gate Installation & Repair' } },
          { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Fence Repair & Post Replacement' } },
        ],
      },
    },
  });

  return (
    <div className="min-h-screen bg-white">

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-8">
              <Link to="/">
                <img
                  src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp"
                  alt="Fix Right Services Logo"
                  width="180"
                  height="56"
                  className="h-14 w-auto"
                  loading="eager"
                />
              </Link>
              <div className="hidden md:flex items-center gap-6 text-sm font-medium text-gray-700">
                <Link to="/" className="hover:text-[#B45309] transition-colors cursor-pointer">Home</Link>
                <Link to="/services" className="text-[#B45309] font-semibold cursor-pointer">Services</Link>
                <Link to="/blog" className="hover:text-[#B45309] transition-colors cursor-pointer">Blog</Link>
              </div>
            </div>
            <a
              href="tel:+12082046331"
              className="flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              aria-label="Call Fix Right Services"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
              <span className="hidden sm:inline">(208) 204-6331</span>
              <span className="sm:hidden">Call Now</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden" aria-label="Fence service hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20fence%20contractor%20installing%20a%20tall%20cedar%20wood%20privacy%20fence%20in%20a%20beautiful%20residential%20backyard%20in%20Boise%20Idaho%20golden%20hour%20sunlight%20warm%20tones%20green%20lawn%20suburban%20neighborhood%20clean%20craftsmanship&width=1920&height=750&seq=fence-hero-bg-02&orientation=landscape"
            alt="Fence Installation in Boise Idaho"
            width="1920"
            height="750"
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-black/60"></div>
        </div>
        <div className="relative z-10 max-w-5xl mx-auto w-full">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-sm text-white/70">
              <li><Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link></li>
              <li><span className="w-4 h-4 inline-flex items-center justify-center"><Icon name="chevron-right" className="w-3 h-3" /></span></li>
              <li><Link to="/services" className="hover:text-white transition-colors cursor-pointer">Services</Link></li>
              <li><span className="w-4 h-4 inline-flex items-center justify-center"><Icon name="chevron-right" className="w-3 h-3" /></span></li>
              <li className="text-white font-medium">Fence Installation &amp; Repair</li>
            </ol>
          </nav>
          <div className="inline-flex items-center gap-2 bg-[#B45309]/90 text-white text-sm font-medium px-4 py-2 rounded-full mb-6">
            <span className="w-4 h-4 flex items-center justify-center"><Icon name="location-dot" className="w-3.5 h-3.5" /></span>
            Boise &amp; Treasure Valley, Idaho
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Fence Installation &amp; Repair in Boise, Idaho
          </h1>
          <p className="text-xl text-white/90 mb-8 max-w-3xl">
            Wood, vinyl, chain link, and custom gates — installed right the first time. Licensed, insured, and trusted by Treasure Valley homeowners since day one.
          </p>
          <div className="flex flex-wrap gap-4 mb-10">
            <a
              href="tel:+12082046331"
              className="inline-flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-8 py-3.5 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
              Call (208) 204-6331
            </a>
            <a
              href="#fence-quote"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-900 px-8 py-3.5 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="file-lines" className="w-4 h-4" /></span>
              Get Free Estimate
            </a>
          </div>
          <div className="flex flex-wrap gap-6 text-white/85 text-sm">
            {[
              { icon: 'clock', text: 'Response within 24 hours' },
              { icon: 'shield-halved', text: 'Licensed & Insured in Idaho' },
              { icon: 'tag', text: 'Free written estimates' },
              { icon: 'star', text: '5-star rated by local homeowners' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center"><Icon name={item.icon} className="w-4 h-4 text-[#F59E0B]" /></span>
                {item.text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" aria-label="Who this service is for">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">Who We Help &amp; Common Fence Problems We Solve</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Whether you&apos;re a <strong>Boise homeowner</strong> looking to add privacy, a <strong>real estate investor</strong> refreshing a fix-and-flip, or a <strong>property manager</strong> dealing with storm damage — Fix Right Services handles fence projects of all sizes across the Treasure Valley.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  { icon: 'house', label: 'Homeowners', desc: 'New installs for privacy, pets, kids, or curb appeal' },
                  { icon: 'building', label: 'Real Estate Investors', desc: 'Fast turnaround fence work for fix-and-flip projects' },
                  { icon: 'key', label: 'Landlords & Property Managers', desc: 'Repairs and replacements between tenants' },
                  { icon: 'store', label: 'Small Business Owners', desc: 'Perimeter fencing for commercial properties' },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                    <span className="w-10 h-10 flex items-center justify-center bg-[#B45309]/10 rounded-lg flex-shrink-0">
                      <Icon name={item.icon} className="w-5 h-5 text-[#B45309]" />
                    </span>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{item.label}</p>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-5">Common Fence Problems We Fix</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  'Leaning or fallen fence posts',
                  'Rotted or warped wood boards',
                  'Storm and wind damage',
                  'Broken or sagging gates',
                  'Rusted chain link sections',
                  'Missing or cracked vinyl panels',
                  'Loose or missing hardware',
                  'Fence sections hit by vehicles',
                  'Old fence needing full replacement',
                  'Uneven or unlevel fence lines',
                  'HOA non-compliant fencing',
                  'Fence blocking drainage or grading',
                ].map((problem) => (
                  <div key={problem} className="flex items-start gap-2 text-sm text-gray-700 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon name="triangle-exclamation" className="w-4 h-4 text-red-400" />
                    </span>
                    {problem}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Materials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" aria-label="Fence materials">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Fence Materials We Install</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Every material has trade-offs. We help you choose the right one for your budget, HOA rules, and long-term goals.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {materials.map((mat) => (
              <article key={mat.name} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="h-52 overflow-hidden">
                  <img
                    src={mat.image}
                    alt={`${mat.name} fence installation Boise Idaho`}
                    width="600"
                    height="400"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-10 h-10 flex items-center justify-center bg-[#B45309]/10 rounded-lg">
                      <Icon name={mat.icon} className="w-5 h-5 text-[#B45309]" />
                    </span>
                    <h3 className="text-xl font-bold text-gray-900">{mat.name}</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">Pros</p>
                      <ul className="space-y-1">
                        {mat.pros.map((p) => (
                          <li key={p} className="flex items-start gap-1.5 text-xs text-gray-700">
                            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="check" className="w-3 h-3 text-green-600" /></span>
                            {p}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2">Cons</p>
                      <ul className="space-y-1">
                        {mat.cons.map((c) => (
                          <li key={c} className="flex items-start gap-1.5 text-xs text-gray-700">
                            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="xmark" className="w-3 h-3 text-red-400" /></span>
                            {c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="border-t border-gray-100 pt-4 space-y-2">
                    <div className="flex items-start gap-2 text-xs text-gray-600">
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="clock" className="w-3 h-3 text-[#B45309]" /></span>
                      <span><strong>Lifespan:</strong> {mat.lifespan}</span>
                    </div>
                    <div className="flex items-start gap-2 text-xs text-gray-600">
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="circle-check" className="w-3 h-3 text-[#B45309]" /></span>
                      <span><strong>Best for:</strong> {mat.bestFor}</span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" aria-label="Fence pricing">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Pricing Drivers &amp; Ballpark Ranges</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Fence costs vary widely. These ranges reflect real-world Boise-area pricing — not national averages. Your actual quote depends on the factors listed.
            </p>
          </div>

          {/* Pricing Drivers */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-10">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-6 h-6 flex items-center justify-center"><Icon name="sliders" className="w-5 h-5 text-[#B45309]" /></span>
              What Drives Fence Costs
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { factor: 'Linear footage', note: 'More fence = more cost, but price per foot often drops on larger jobs' },
                { factor: 'Material choice', note: 'Chain link &lt; wood &lt; vinyl in upfront cost; vinyl wins long-term on maintenance' },
                { factor: 'Fence height', note: 'Taller fences require more material and deeper posts' },
                { factor: 'Site conditions', note: 'Rocky soil, slopes, or tree roots increase labor time significantly' },
                { factor: 'Old fence removal', note: 'Demo and haul-away adds cost; DIY removal can save money' },
                { factor: 'Gates', note: 'Each gate adds cost — especially double drive gates or automatic openers' },
                { factor: 'Post depth & concrete', note: 'Deeper frost lines or soft soil require more concrete per post' },
                { factor: 'HOA requirements', note: 'Specific materials, colors, or styles may limit options and affect cost' },
                { factor: 'Permit fees', note: 'City/county permit fees vary; typically $50–$200 for residential fences' },
              ].map((item) => (
                <div key={item.factor} className="bg-white rounded-lg p-3 border border-amber-100">
                  <p className="font-semibold text-gray-900 text-sm">{item.factor}</p>
                  <p className="text-gray-600 text-xs mt-0.5" dangerouslySetInnerHTML={{ __html: item.note }} />
                </div>
              ))}
            </div>
          </div>

          {/* Pricing Table */}
          <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="text-left px-5 py-4 font-semibold">Fence Type / Service</th>
                  <th className="text-left px-5 py-4 font-semibold">Low End</th>
                  <th className="text-left px-5 py-4 font-semibold">High End</th>
                  <th className="text-left px-5 py-4 font-semibold hidden md:table-cell">Depends On</th>
                </tr>
              </thead>
              <tbody>
                {pricingRows.map((row, idx) => (
                  <tr key={row.type} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-5 py-4 font-medium text-gray-900">
                      {row.type}
                      <span className="block text-xs text-gray-500 font-normal">{row.unit}</span>
                    </td>
                    <td className="px-5 py-4 text-green-700 font-semibold">{row.low}</td>
                    <td className="px-5 py-4 text-[#B45309] font-semibold">{row.high}</td>
                    <td className="px-5 py-4 text-gray-600 text-xs hidden md:table-cell">{row.dependsOn}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-4 text-center">
            * Ranges reflect Boise/Treasure Valley market conditions as of 2025. Material costs fluctuate. Your written estimate will reflect current pricing.
          </p>

          <div className="mt-8 bg-[#B45309]/5 border border-[#B45309]/20 rounded-xl p-6 text-center">
            <p className="text-gray-700 font-medium mb-3">The only way to know your real cost is a free on-site estimate.</p>
            <a
              href="#fence-quote"
              className="inline-flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="file-lines" className="w-4 h-4" /></span>
              Request Your Free Estimate
            </a>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" aria-label="Fence installation timeline">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Timeline Expectations</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Here&apos;s what a typical fence installation looks like from first contact to finished project — and what can slow things down.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Typical Timeline */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-6">Typical Project Timeline</h3>
              <div className="space-y-4">
                {[
                  { phase: 'Contact & Scheduling', time: 'Day 1–2', desc: 'You reach out, we respond within 24 hours and schedule an on-site visit.' },
                  { phase: 'On-Site Estimate', time: 'Day 2–5', desc: 'We visit, measure, assess site conditions, and discuss material options.' },
                  { phase: 'Written Estimate Delivered', time: 'Within 24–48 hrs of visit', desc: 'You receive a detailed, itemized written estimate.' },
                  { phase: '811 Utility Locate', time: '2–3 business days', desc: 'Required by Idaho law before any digging. We coordinate this.' },
                  { phase: 'HOA / Permit (if needed)', time: '1–30+ days', desc: 'Varies widely. We advise; you submit to HOA. Permits handled by us.' },
                  { phase: 'Material Procurement', time: '1–5 days', desc: 'Most materials are locally stocked. Custom orders may take longer.' },
                  { phase: 'Installation', time: '1–3 days', desc: 'Most residential fences install in 1–3 days once we start.' },
                  { phase: 'Final Walkthrough', time: 'Same day as completion', desc: 'We walk the finished fence with you before we consider the job done.' },
                ].map((step, idx) => (
                  <div key={step.phase} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-[#B45309] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {idx + 1}
                      </div>
                      {idx < 7 && <div className="w-0.5 bg-[#B45309]/20 flex-1 mt-1 min-h-[1.5rem]"></div>}
                    </div>
                    <div className="pb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-gray-900 text-sm">{step.phase}</p>
                        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full whitespace-nowrap">{step.time}</span>
                      </div>
                      <p className="text-gray-600 text-sm">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Delay Factors */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-6">What Can Delay Your Project</h3>
              <div className="space-y-4">
                {delayFactors.map((item) => (
                  <div key={item.title} className="bg-white rounded-xl p-5 border border-gray-200 hover:shadow-sm transition-shadow">
                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0">
                        <Icon name={item.icon} className="w-5 h-5 text-amber-600" />
                      </span>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm mb-1">{item.title}</p>
                        <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Permits Disclaimer */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" aria-label="Permits and rules">
        <div className="max-w-4xl mx-auto">
          <div className="bg-amber-50 border-l-4 border-[#B45309] rounded-r-2xl p-8">
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 flex items-center justify-center bg-[#B45309]/10 rounded-lg flex-shrink-0 mt-1">
                <Icon name="triangle-exclamation" className="w-5 h-5 text-[#B45309]" />
              </span>
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3">Permits, HOA Rules &amp; Local Regulations</h2>
                <div className="space-y-3 text-gray-700 text-sm leading-relaxed">
                  <p>
                    <strong>Permit requirements vary by city and zone.</strong> In Boise, fences under 6 feet in rear/side yards typically don&apos;t require a permit, but front yard fences and anything over 6 feet usually do. Meridian, Nampa, Caldwell, and other Treasure Valley cities have their own rules.
                  </p>
                  <p>
                    <strong>HOA rules are separate from city permits.</strong> Even if the city doesn&apos;t require a permit, your HOA may restrict fence height, material, color, and setbacks. Always check with your HOA before starting.
                  </p>
                  <p>
                    <strong>Idaho law requires calling 811 before any digging.</strong> This is not optional — it&apos;s the law. We coordinate utility locates as part of every fence installation.
                  </p>
                  <p>
                    <strong>We are not your legal or permitting authority.</strong> The information above is general guidance. Always verify current requirements with your local jurisdiction before starting any fence project.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3 mt-5">
                  <a
                    href="https://www.cityofboise.org/departments/planning-and-development-services/"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white border border-[#B45309] text-[#B45309] hover:bg-[#B45309] hover:text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><Icon name="arrow-up-right-from-square" className="w-3.5 h-3.5" /></span>
                    Boise Planning &amp; Development
                  </a>
                  <a
                    href="https://www.idaho811.com/"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 hover:border-[#B45309] hover:text-[#B45309] px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><Icon name="arrow-up-right-from-square" className="w-3.5 h-3.5" /></span>
                    Idaho 811 — Call Before You Dig
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-[#B45309]" aria-label="Call to action">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-white font-bold text-xl mb-1">Ready to get your fence installed?</p>
            <p className="text-white/85 text-sm">Free on-site estimates. Response within 24 hours. No pressure.</p>
          </div>
          <div className="flex flex-wrap gap-3 flex-shrink-0">
            <a
              href="tel:+12082046331"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-[#B45309] px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
              Call (208) 204-6331
            </a>
            <a
              href="#fence-quote"
              className="inline-flex items-center gap-2 border-2 border-white text-white hover:bg-white/10 px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              Request Free Quote
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" aria-label="Frequently asked questions">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Fence Installation FAQs</h2>
            <p className="text-gray-600">Quick answers first — click to read the full explanation.</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleFaq(idx)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                  aria-expanded={openFaq === idx}
                >
                  <div className="pr-4">
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">
                      <a id={`faq-fence-${idx}`}>{faq.q}</a>
                    </h4>
                    <p className="text-[#B45309] text-sm font-medium">{faq.shortA}</p>
                  </div>
                  <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-[#B45309]">
                    <Icon name={openFaq === idx ? 'chevron-up' : 'chevron-down'} className="w-4 h-4" />
                  </span>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5">
                    <div className="border-t border-gray-100 pt-4">
                      <p className="text-gray-600 text-sm leading-relaxed">{faq.fullA}</p>
                      {!expandedFaq.has(idx) && faq.fullA.length > 200 && (
                        <button
                          type="button"
                          onClick={() => toggleExpand(idx)}
                          className="text-[#B45309] text-xs font-medium mt-2 hover:underline cursor-pointer"
                        >
                          Read more
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                'mainEntity': faqs.map((faq) => ({
                  '@type': 'Question',
                  'name': faq.q,
                  'acceptedAnswer': {
                    '@type': 'Answer',
                    'text': faq.shortA + ' ' + faq.fullA,
                  },
                })),
              }),
            }}
          />
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" id="fence-quote" aria-label="Request a fence quote">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Get Your Free Fence Estimate</h2>
            <p className="text-gray-600">
              Tell us about your project and we&apos;ll get back to you within 24 hours with a free, no-obligation estimate.
            </p>
            <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-500">
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4 text-[#B45309]" /></span>
              Prefer to call?&nbsp;
              <a href="tel:+12082046331" className="text-[#B45309] font-semibold hover:underline cursor-pointer">(208) 204-6331</a>
            </div>
          </div>
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-8">
            <FenceQuoteForm formId="fence-page-quote-form" />
          </div>
        </div>
      </section>

      {/* Related Blog Posts */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" aria-label="Related fence resources">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">More Fence Resources for Boise Homeowners</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: 'Fence Installation Cost in Boise, Idaho', slug: '/blog/fence-installation-cost-boise-idaho', desc: 'Real cost breakdowns for wood, vinyl, and chain link fences in the Treasure Valley.' },
              { title: 'Wood vs. Vinyl Fence: Treasure Valley Guide', slug: '/blog/wood-vs-vinyl-fence-treasure-valley', desc: 'Side-by-side comparison to help you choose the right material for your property.' },
              { title: 'Best Fence Styles for Boise Neighborhoods', slug: '/blog/best-fence-styles-boise-neighborhoods', desc: 'Which fence styles work best in North End, Meridian, Eagle, and beyond.' },
              { title: 'Fence Installation Timeline in Boise', slug: '/blog/fence-installation-timeline-boise', desc: 'What to expect from first call to finished fence — week by week.' },
              { title: 'Signs Your Boise Fence Needs Replacement', slug: '/blog/signs-boise-fence-needs-replacement', desc: 'How to tell if repair is enough or if it\'s time for a full replacement.' },
              { title: 'Why Boise Homeowners Are Upgrading Fences in 2025', slug: '/blog/why-boise-homeowners-upgrading-fences-2025', desc: 'Trends, ROI data, and the top reasons Treasure Valley homeowners are investing in new fences.' },
            ].map((post) => (
              <Link
                key={post.slug}
                to={post.slug}
                className="bg-white rounded-xl p-5 border border-gray-200 hover:shadow-md hover:border-[#B45309]/30 transition-all cursor-pointer group"
              >
                <h3 className="font-semibold text-gray-900 text-sm mb-2 group-hover:text-[#B45309] transition-colors">{post.title}</h3>
                <p className="text-gray-600 text-xs leading-relaxed mb-3">{post.desc}</p>
                <span className="text-[#B45309] text-xs font-semibold flex items-center gap-1">
                  Read article
                  <span className="w-4 h-4 flex items-center justify-center"><Icon name="arrow-right" className="w-3 h-3" /></span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp" alt="Fix Right Services" width="180" height="48" className="h-12 w-auto mb-4" loading="lazy" />
              <p className="text-gray-400 text-sm">Boise&apos;s trusted fence installation and repair experts serving the entire Treasure Valley.</p>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Fence Services</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>Wood Fence Installation</li>
                <li>Vinyl Fence Installation</li>
                <li>Chain Link Fencing</li>
                <li>Gate Installation &amp; Repair</li>
                <li>Fence Post Replacement</li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Resources</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">All Services</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Blog &amp; Tips</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Contact</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="phone" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <a href="tel:+12082046331" className="hover:text-white transition-colors cursor-pointer">(208) 204-6331</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="envelope" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <a href="mailto:Southernkole@gmail.com" className="hover:text-white transition-colors cursor-pointer">Southernkole@gmail.com</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="location-dot" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <span>Boise, Meridian, Nampa &amp; Treasure Valley</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>&copy; 2025 Fix Right Services. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
